import { decryptPayload } from '../../payment-gateways/core/utils/crypto';
import {
  PaymentError,
  PaymentErrorCode,
} from '../../payment-gateways/core/utils/errors';
import { savePendingOrder } from '../../payment-gateways/core/utils/pendingOrderStore';
import { normalizeCountry, resolveCountry } from '../../country/countryConfig';
import {
  ENTRY_PARAMS,
  isSupportedPayloadVersion,
  type EntryPayload,
} from '../../contracts/payment-contract';
import type { OrderContext } from '../../payment-gateways/core/types';

/**
 * Either an order to pay, or the reason we can't build one. There is no
 * third case — that's the point. The old Payment.jsx could reach a state
 * where it had neither and simply rendered a header over nothing (the
 * plan's §1, Critical), so this type is what makes the blank page
 * unrepresentable rather than merely unlikely.
 */
export type EntryResolution =
  | { ok: true; order: OrderContext }
  | { ok: false; error: PaymentError };

/**
 * Reads the booking app's entry redirect — `?payment_id=...&p=<encrypted>`
 * — and turns it into an OrderContext. Takes the search string and
 * hostname as arguments rather than reading `window` itself, so the whole
 * decrypt → version-check → country-resolution chain is testable without
 * a browser.
 *
 * It does write to sessionStorage (savePendingOrder) as a deliberate
 * exception: the callback route is reached by a real browser navigation
 * and needs the auth token that only lives here. That write has to happen
 * during render, before any gateway can redirect away — a child effect in
 * GatewayFactory runs *before* a parent effect in PaymentPage, so moving
 * this into a useEffect would let a hosted checkout leave the page with
 * nothing stored.
 */
export function resolveEntryOrder(
  search: URLSearchParams,
  hostname: string
): EntryResolution {
  const orderId = search.get(ENTRY_PARAMS.orderId);
  const encrypted = search.get(ENTRY_PARAMS.encryptedPayload);

  if (!orderId || !encrypted) {
    return fail(
      PaymentErrorCode.INVALID_ORDER,
      'This payment link is missing required details.'
    );
  }

  const payload = decryptPayload<EntryPayload>(encrypted);

  if (
    !payload ||
    !isSupportedPayloadVersion(payload) ||
    (payload.redirectUrl !== undefined &&
      (typeof payload.redirectUrl !== 'string' ||
        payload.redirectUrl.trim().length === 0))
  ) {
    return fail(
      PaymentErrorCode.CONFIG_ERROR,
      'We could not verify this payment link.',
      orderId
    );
  }

  // Hostname is the source of truth (each country is its own deployment);
  // the payload's country is only a fallback for the rare case hostname
  // resolution itself can't determine one (e.g. a preview/staging host).
  const country =
    resolveCountry(hostname, search) ?? normalizeCountry(payload.country);

  if (!country) {
    return fail(
      PaymentErrorCode.CONFIG_ERROR,
      'We could not determine which region this payment belongs to.',
      orderId
    );
  }

  const order: OrderContext = {
    orderId,
    redirectUrl: payload.redirectUrl,
    authToken: payload.authToken,
    dbPtr: payload.db_ptr,
    amount: payload.amount,
    country,
  };

  savePendingOrder(order);

  return { ok: true, order };
}

function fail(
  code: PaymentErrorCode,
  message: string,
  orderId?: string
): EntryResolution {
  return { ok: false, error: new PaymentError(code, message, { orderId }) };
}
